import type { CanvasEx } from '@/typings/canvas';
import type { ListElements, ElementType } from '@/typings/customilyElements/layer';
import { ListElementType } from '@/typings/customilyElements/listElementType';
import { createGroup } from '@/utils/elementsGroupUtils';
import { ElementsGroup } from '@/typings/customilyElements/elementsGroup';

export const getFlatChildrenByItem = (item: ListElements, prependParent = true): ListElements[] => {
    const getChildrenRecursively = (items: ListElements[]): ListElements[] => {
        let flatterElements: ListElements[] = [];

        for (const item of items) {
            flatterElements.push(item);
            if (item.children && item.children.length > 0) {
                flatterElements = flatterElements.concat(getChildrenRecursively(item.children));
            }
        }

        return flatterElements;
    };

    const children = getChildrenRecursively(item.children || []);
    return prependParent ? [item, ...children] : children;
};

export const canGroupSelectedItems = (canvas: CanvasEx) => {
    const selectedItems = canvas._selectedItems;
    if (!selectedItems?.length) {
        return false;
    }

    return selectedItems.every((item) => {
        // For CanvasElements and Groups
        if (item.type === ListElementType.CanvasElement || item.type === ListElementType.Group) {
            return true;
        }

        return false;
    });
};

export const canUnGroupSelectedItems = (canvas: CanvasEx) => {
    const selectedItems = canvas._selectedItems;
    if (!selectedItems?.length) return false;
    return selectedItems.every((item) => item.type === ListElementType.Group);
};

const isElementType = (item: ListElements | ElementType) => Object.hasOwnProperty.call(item, 'groupId');

export const groupItemsDeep = async (
    items: ListElements[] | ElementType[],
    options: {
        canvas: CanvasEx;
        saveHistory?: boolean;
        name?: string;
        spotifyPlayer?: boolean;
        isLayout: boolean;
    }
) => {
    try {
        if (!items?.length) return;

        const { canvas, saveHistory, name, spotifyPlayer, isLayout } = options;

        // Skip grouping check if spotifyPlayer is true
        if (!spotifyPlayer && !canGroupSelectedItems(canvas)) return;

        const firstElement = items[0];
        if (!firstElement) return;

        let parentId = null;
        if (firstElement.type === ListElementType.Group) {
            parentId = firstElement.element.parentId;
        } else if (firstElement.type === ListElementType.CanvasElement) {
            parentId = firstElement.element.groupId;
        } else if (isElementType(firstElement)) {
            parentId = firstElement.groupId;
        }
        const newGroup = createGroup(canvas, {
            type: canvas.isPreviewStep ? 'preview' : 'eps',
            zIndex: canvas.getHighestZIndex() + 1,
            parentId,
            name,
            spotifyPlayer: spotifyPlayer || undefined,
            isLayout
        });

        if (!newGroup) return;

        canvas.addGroup(newGroup);

        const newGroupId = newGroup.id;
        items.forEach((item) => {
            if (item.type === ListElementType.Group) {
                item.element.parentId = newGroupId;
            } else if (item.type === ListElementType.CanvasElement) {
                item.element.groupId = newGroupId;
            } else if (isElementType(item)) {
                item.groupId = newGroupId;
            }
        });

        if (saveHistory) {
            canvas.undoManager?.add(
                (newGroup: ElementsGroup, items: (ListElements | ElementType)[]) => {
                    canvas.addGroup(newGroup);
                    items.forEach((item) => {
                        if (item.type === ListElementType.Group) {
                            item.element.parentId = newGroup.id;
                        } else if (item.type === ListElementType.CanvasElement) {
                            item.element.groupId = newGroup.id;
                        } else if (isElementType(item)) {
                            item.groupId = newGroup.id;
                        }
                    });
                },
                [newGroup, items],
                (newGroup: ElementsGroup, items: (ListElements | ElementType)[]) => {
                    canvas.removeGroup(newGroup);
                    items.forEach((item) => {
                        if (item.type === ListElementType.Group) {
                            item.element.parentId = null;
                        } else if (item.type === ListElementType.CanvasElement) {
                            item.element.groupId = null;
                        } else if (isElementType(item)) {
                            item.groupId = null;
                        }
                    });
                },
                [newGroup, items]
            );
        }

        setTimeout(() => {
            canvas.fire('object:grouped', [newGroup]);
        });

        return newGroup;
    } catch (error) {
        console.error('Error grouping selected elements:', error);
    }
};

export const unGroupItemsDeep = async (
    items: ListElements[],
    options: {
        canvas: CanvasEx;
        saveHistory?: boolean;
    }
) => {
    try {
        if (!items?.length) return;

        const { canvas, saveHistory } = options;
        if (!canUnGroupSelectedItems(canvas)) return;

        const selectedGroups = items.filter((item) => item.type === ListElementType.Group);
        const newSelectedElements: ElementType[] = [];
        const oldGroups: ElementsGroup[] = [];
        const childGroupMap: Map<ElementType, ElementsGroup> = new Map();

        selectedGroups.forEach((item) => {
            const children = item.children;
            if (!children) return;

            oldGroups.push(item.element);
            children.forEach((child) => {
                newSelectedElements.push(child.element);
                childGroupMap.set(child, item.element);
                if (child.type === ListElementType.Group) {
                    child.element.parentId = item?.element?.parentId ? item.element.parentId : null;
                } else if (child.type === ListElementType.CanvasElement) {
                    const parentId = item?.element?.parentId;
                    child.element.groupId = parentId ? parentId : undefined;
                }
            });
            canvas.removeGroup(item.element);
        });

        if (saveHistory) {
            const undoRedoCallback = (
                groups: ElementsGroup[],
                map: Map<ElementType, ElementsGroup>,
                isUndo: boolean
            ) => {
                groups.forEach((group) => {
                    if (isUndo) {
                        canvas.addGroup(group);
                    } else {
                        canvas.removeGroup(group);
                    }
                });
                map.forEach((group, child) => {
                    if (child.type === ListElementType.Group) {
                        child.element.parentId = isUndo ? group.id : null;
                    } else if (child.type === ListElementType.CanvasElement) {
                        child.element.groupId = isUndo ? group.id : null;
                    }
                });
            };

            canvas.undoManager?.add(
                (groups, map) => undoRedoCallback(groups, map, false),
                [oldGroups, childGroupMap],
                (groups, map) => undoRedoCallback(groups, map, true),
                [oldGroups, childGroupMap]
            );
        }

        setTimeout(() => {
            canvas.fire('object:ungrouped', newSelectedElements);
        });
    } catch (error) {
        console.error('Error un-grouping selected elements:', error);
    }
};

export const collectAncestorGroupIds = (item: ListElements): string[] => {
    const groupIds: string[] = [];
    let ancestor: ListElements | undefined = item.parent;

    while (ancestor) {
        if (ancestor.type === ListElementType.Group) {
            groupIds.push(ancestor.element.uuid ?? ancestor.element.id);
        }
        ancestor = ancestor.parent;
    }

    return groupIds;
};

export const getSelectedGroup = (canvas: CanvasEx): ElementsGroup | null => {
    const selectedItems = canvas._selectedItems;
    if (!selectedItems?.length) return null;
    const firstElement = selectedItems[0];
    if (!firstElement) return null;
    const isGroup = firstElement.type === ListElementType.Group;
    if (!isGroup) {
        return null;
    }
    return firstElement.element as ElementsGroup;
}